<template>
  <div class="upload-modal" v-if="showUploadModal">
    <div class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <div class="upload-content">
          <div class="upload-header">
            <h3>Загрузка пользовательских данных</h3>
            <button class="close-btn" @click="closeModal">×</button>
          </div>

          <div class="upload-body">
            <div class="upload-steps">
              <!-- Шаг 1: Выбор режима работы -->
              <div v-if="currentStep === 1" class="upload-step">
                <h4>Выберите тип обработки:</h4>
                <div class="work-mode-selector">
                  <div 
                    v-for="mode in workModes" 
                    :key="mode.value"
                    class="work-mode-card"
                    :class="{ 'selected': selectedWorkMode === mode.value }"
                    @click="selectWorkMode(mode.value)"
                  >
                    <div class="mode-icon">{{ mode.icon }}</div>
                    <div class="mode-info">
                      <h5>{{ mode.label }}</h5>
                      <p>{{ mode.description }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Режим 1: ZIP + JSON/Excel -->
              <div v-if="selectedWorkMode === 'zip_data' && currentStep >= 2">
                <!-- Шаг 2: Загрузка файлов -->
                <div v-if="currentStep === 2" class="upload-step">
                  <h4>Загрузка ZIP архива и данных</h4>
                  
                  <div class="file-upload-sections">
                    <!-- Загрузка ZIP -->
                    <div class="upload-section">
                      <h5>ZIP архив с изображениями</h5>
                      <div class="file-drop-zone" 
                          @drop="handleZipDrop"
                          @dragover.prevent
                          @dragenter.prevent>
                        <input type="file" 
                              ref="zipFileInput"
                              @change="handleZipSelect"
                              accept=".zip"
                              style="display: none">
                        
                        <div class="drop-zone-content">
                          <div class="upload-icon">📦</div>
                          <p v-if="!zipFile">Перетащите ZIP архив сюда или нажмите для выбора</p>
                          <p v-else class="file-selected">
                            ✅ Архив выбран: {{ zipFile.name }} ({{ formatFileSize(zipFile.size) }})
                          </p>
                          <button @click="triggerZipInput" class="select-btn">
                            {{ zipFile ? 'Заменить архив' : 'Выбрать архив' }}
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Загрузка данных -->
                    <div class="upload-section">
                      <h5>Файл с координатами (GeoJSON/Excel)</h5>
                      <div class="file-drop-zone" 
                          @drop="handleDataFileDrop"
                          @dragover.prevent
                          @dragenter.prevent>
                        <input type="file" 
                              ref="dataFileInput"
                              @change="handleDataFileSelect"
                              accept=".json,.geojson,.xlsx,.xls,.csv"
                              style="display: none">
                        
                        <div class="drop-zone-content">
                          <div class="upload-icon">📊</div>
                          <p v-if="!dataFile">Перетащите файл с координатами или нажмите для выбора</p>
                          <p v-else class="file-selected">
                            ✅ Файл выбран: {{ dataFile.name }} ({{ formatFileSize(dataFile.size) }})
                          </p>
                          <button @click="triggerDataFileInput" class="select-btn">
                            {{ dataFile ? 'Заменить файл' : 'Выбрать файл' }}
                          </button>
                          <p class="file-types">Поддерживаемые форматы: GeoJSON, Excel, CSV</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="upload-info">
                    <p>📁 После загрузки архив будет автоматически распакован на сервере и выполнено автоматическое сопоставление.</p>
                  </div>
                </div>

                <!-- Шаг 3: Предпросмотр сопоставленных данных -->
                <div v-if="currentStep === 3" class="upload-step">
                  <h4>Предпросмотр сопоставленных данных</h4>
                  
                  <div v-if="matchedData.length > 0" class="preview-container">
                    <div class="preview-header">
                      <h5>Найдено сопоставлений: {{ matchedData.length }}</h5>
                      <div class="preview-controls">
                        <input type="text" v-model="searchQuery" placeholder="Поиск по имени файла..." />
                      </div>
                    </div>

                    <!-- Горизонтальная таблица -->
                    <div class="results-table-container">
                      <table class="results-table">
                        <thead>
                          <tr>
                            <th class="col-image">Данные пользователя<br>(изображение + координаты)</th>
                            <th class="col-addresses">Адреса рядом</th>
                            <th class="col-buildings">Здания рядом</th>
                            <th class="col-segmentation">Карта сегментации</th>
                            <th class="col-annotations">Аннотации</th>
                            <th class="col-building-address">Вероятный адрес здания</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in filteredMatchedData" :key="item.image_name">
                            <!-- Столбец 1: Данные пользователя -->
                            <td class="user-data-cell">
                              <div class="user-data-content">
                                <div class="image-thumbnail-table">
                                  <img :src="item.thumbnail_url" :alt="item.image_name" @error="handleImageError">
                                </div>
                                <div class="file-info-table">
                                  <div class="file-name-table">{{ item.image_name }}</div>
                                  <div class="coordinates-table" v-if="item.lat && item.lon">
                                    <div class="coord-value-table">
                                      {{ item.lat.toFixed(6) }}, {{ item.lon.toFixed(6) }}
                                    </div>
                                  </div>
                                  <div class="file-size-table" v-if="item.file_size">
                                    {{ formatFileSize(item.file_size) }}
                                  </div>
                                </div>
                              </div>
                            </td>
                            
                            <!-- Столбец 2: Адреса рядом -->
                            <td class="addresses-cell">
                              <div v-if="item.nearby_addresses && item.nearby_addresses.length > 0" class="addresses-list">
                                <div v-for="(address, idx) in item.nearby_addresses.slice(0, 3)" :key="idx" class="address-item">
                                  {{ address }}
                                </div>
                                <div v-if="item.nearby_addresses.length > 3" class="more-addresses">
                                  +{{ item.nearby_addresses.length - 3 }} еще...
                                </div>
                              </div>
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-text">Нет данных об адресах</div>
                              </div>
                            </td>
                            
                            <!-- Столбец 3: Здания рядом -->
                            <td class="buildings-cell">
                              <div v-if="item.nearby_buildings && item.nearby_buildings.length > 0" class="buildings-list">
                                <div v-for="(building, idx) in item.nearby_buildings.slice(0, 5)" :key="idx" class="building-item">
                                  <div class="building-name">{{ building.name }}</div>
                                  <div class="building-distance" v-if="building.distance">{{ building.distance }}м</div>
                                  <!-- КООРДИНАТЫ ЗДАНИЯ -->
                                  <div class="building-coordinates" v-if="building.tags">
                                    <small>{{ building.tags.lat?.toFixed(6) }}, {{ building.tags.lon?.toFixed(6) }}</small>
                                  </div>
                                  <div class="building-images" v-if="building.images && building.images.length > 0">
                                    <img v-for="(img, imgIdx) in building.images.slice(0, 1)" 
                                         :key="imgIdx" 
                                         :src="img.thumbnail_url" 
                                         :alt="building.name"
                                         class="building-thumbnail"
                                         @error="handleImageError">
                                  </div>
                                </div>
                                <div v-if="item.nearby_buildings.length > 5" class="more-buildings">
                                  +{{ item.nearby_buildings.length - 5 }} зданий
                                </div>
                              </div>
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-text">Здания не найдены</div>
                              </div>
                            </td>
                            
                            <!-- Столбец 4: Карта сегментации -->
                            <td class="segmentation-cell">
                              <div v-if="item.segmentation_map" class="segmentation-preview">
                                <img :src="item.segmentation_map" alt="Карта сегментации" @error="handleImageError">
                              </div>
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-icon">🖼️</div>
                                <div class="placeholder-text">Сегментация</div>
                              </div>
                            </td>
                            
                            <!-- Столбец 5: Аннотации -->
                            <td class="segmentation-cell">
                              <div v-if="item.annotations" class="segmentation-preview">
                                <img :src="item.annotations" alt="Аннотации" @error="handleImageError">
                              </div>
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-icon">🖼️</div>
                                <div class="placeholder-text">Аннотации</div>
                              </div>
                            </td>
                            
                            <!-- Столбец 6: Вероятный адрес здания -->
                            <td class="address-cell">
                              <div v-if="item.building_address" class="address-result">
                                <!-- Основной адрес -->
                                <div class="main-address">
                                  <strong>{{ item.building_address.street }}</strong>
                                  <span class="confidence-badge" :class="getConfidenceClass(item.building_address.confidence)">
                                    {{ (item.building_address.confidence * 100).toFixed(1) }}%
                                  </span>
                                </div>
                                
                                <!-- КООРДИНАТЫ АДРЕСА -->
                                <div class="coordinates-info" v-if="item.building_address.coordinates">
                                  <small>Координаты: {{ formatCoordinates(item.building_address.coordinates) }}</small>
                                </div>
                                
                                <!-- Топ-3 совпадения -->
                                <div v-if="item.building_address.top_3_matches && item.building_address.top_3_matches.length > 0" 
                                     class="top-matches">
                                  <div class="matches-title">Лучшие совпадения:</div>
                                  <div v-for="(match, index) in item.building_address.top_3_matches" 
                                       :key="index" class="match-item">
                                    <div class="match-main">
                                      <span class="match-name">{{ match.name }}</span>
                                      <span class="match-confidence">{{ match.confidence }}</span>
                                    </div>
                                    <div v-if="match.distance" class="match-distance">
                                      {{ match.distance }}
                                    </div>
                                    <!-- КООРДИНАТЫ ДЛЯ КАЖДОГО СОВПАДЕНИЯ -->
                                    <div v-if="match.coordinates" class="match-coords">
                                      <small>{{ formatCoordinates(match.coordinates) }}</small>
                                    </div>
                                  </div>
                                </div>
                                
                                <!-- Метод поиска -->
                                <div class="search-method">
                                  <small>Метод: {{ getMethodName(item.building_address.method) }}</small>
                                </div>
                              </div>
                              
                              <div v-else-if="item.address_search_failed" class="placeholder-cell error">
                                <div class="placeholder-icon">❌</div>
                                <div class="placeholder-text">Адрес не найден</div>
                              </div>
                              
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-icon">🏠</div>
                                <div class="placeholder-text">Поиск адреса...</div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div class="preview-actions">
                      <button @click="startZipProcessing" 
                              :disabled="processing" 
                              class="process-btn primary">
                        {{ processing ? 'Обработка...' : `Запустить обработку (${matchedData.length} изображений)` }}
                      </button>
                      <button @click="currentStep = 2" class="process-btn secondary">
                        Назад к загрузке
                      </button>
                    </div>
                  </div>
                  
                  <div v-else class="no-data-message">
                    <p>Не удалось сопоставить данные. Проверьте соответствие имен файлов и записей в данных.</p>
                    <button @click="currentStep = 2" class="nav-btn">Назад к загрузке</button>
                  </div>
                </div>

                <!-- Шаг 4: Результаты обработки -->
                <div v-if="currentStep === 4" class="upload-step">
                  <h4>Результаты обработки</h4>
                  
                  <div class="results-container">
                    <div class="results-summary">
                      <h5>✅ Обработано изображений: {{ matchedData.length }}</h5>
                      <p>Все данные успешно обработаны. Вы можете скачать результаты в Excel.</p>
                    </div>

                    <!-- Таблица с результатами -->
                    <div class="results-table-container">
                      <table class="results-table">
                        <thead>
                          <tr>
                            <th class="col-image">Изображение</th>
                            <th class="col-addresses">Адреса рядом</th>
                            <th class="col-buildings">Здания рядом</th>
                            <th class="col-segmentation">Сегментация</th>
                            <th class="col-annotations">Аннотации</th>
                            <th class="col-building-address">Вероятный адрес</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in filteredMatchedData" :key="item.image_name">
                            <td class="user-data-cell">
                              <div class="user-data-content">
                                <div class="image-thumbnail-table">
                                  <a :href="item.thumbnail_url" target="_blank" rel="noopener noreferrer">
                                    <img :src="item.thumbnail_url" :alt="item.image_name" @error="handleImageError">
                                  </a>
                                </div>
                                <div class="file-info-table">
                                  <div class="file-name-table">{{ item.image_name }}</div>
                                  <div class="coordinates-table" v-if="item.lat && item.lon">
                                    <div class="coord-value-table">
                                      {{ item.lat.toFixed(6) }}, {{ item.lon.toFixed(6) }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                            
                            <td class="addresses-cell">
                              <div v-if="item.nearby_addresses && item.nearby_addresses.length > 0" class="addresses-list">
                                <div v-for="(address, idx) in item.nearby_addresses.slice(0, 3)" :key="idx" class="address-item">
                                  {{ address }}
                                </div>
                              </div>
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-text">—</div>
                              </div>
                            </td>
                            
                            <td class="buildings-cell">
                              <div v-if="item.nearby_buildings && item.nearby_buildings.length > 0" class="buildings-list">
                                <div v-for="(building, idx) in item.nearby_buildings.slice(0, 2)" :key="idx" class="building-item">
                                  <div class="building-name">{{ building.name }}</div>
                                  <div class="building-distance" v-if="building.distance">{{ building.distance }}м</div>
                                  <!-- КООРДИНАТЫ ЗДАНИЯ -->
                                  <div class="building-coordinates" v-if="building.tags">
                                    <small>{{ building.tags.lat?.toFixed(6) }}, {{ building.tags.lon?.toFixed(6) }}</small>
                                  </div>
                                </div>
                              </div>
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-text">—</div>
                              </div>
                            </td>
                            
                            <td class="segmentation-cell">
                              <div v-if="item.segmentation_map" class="segmentation-preview">
                                <a :href="item.segmentation_map" target="_blank" rel="noopener noreferrer">
                                  <img :src="item.segmentation_map" alt="Сегментация" @error="handleImageError">
                                </a>
                              </div>
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-text">—</div>
                              </div>
                            </td>
                            
                            <td class="segmentation-cell">
                              <div v-if="item.annotations" class="segmentation-preview">
                                <a :href="item.annotations" target="_blank" rel="noopener noreferrer">
                                  <img :src="item.annotations" alt="Аннотации" @error="handleImageError">
                                </a>
                              </div>
                              <div v-else class="placeholder-cell">
                                <div class="placeholder-text">—</div>
                              </div>
                            </td>
                            
                            <!-- Столбец 6: Вероятный адрес здания -->
<!-- Столбец 6: Вероятный адрес здания -->
<td class="building-address-cell">
  <div v-if="item.building_address" class="building-address">
    <!-- Основной адрес -->
    <div class="address-main">{{ item.building_address.street }}</div>
    
    <!-- КООРДИНАТЫ АДРЕСА -->
    <div class="address-coordinates" v-if="item.building_address.coordinates">
      <small>{{ formatCoordinates(item.building_address.coordinates) }}</small>
    </div>
    
    <!-- Топ-3 совпадения -->
    <div v-if="item.building_address.top_3_matches && item.building_address.top_3_matches.length > 0" 
         class="top-matches">
      <div class="matches-title">Лучшие совпадения:</div>
      <div v-for="(match, index) in item.building_address.top_3_matches" 
           :key="index" class="match-item">
        <div class="match-name">{{ match.name }}</div>
        <div class="match-confidence">{{ match.confidence }}</div>
        <div v-if="match.distance" class="match-distance">{{ match.distance }}</div>
        <!-- КООРДИНАТЫ ДЛЯ КАЖДОГО СОВПАДЕНИЯ - ИСПРАВЛЕННАЯ СТРОКА -->
        <div class="match-coordinates" v-if="match.coordinates">
          <small>{{ formatCoordinates(match.coordinates) }}</small>
        </div>
      </div>
    </div>
    
    <div class="address-confidence" v-if="item.building_address.confidence">
      Уверенность: {{ (item.building_address.confidence * 100).toFixed(0) }}%
    </div>
    <div class="address-method" v-if="item.building_address.method">
      <small>Метод: {{ getMethodName(item.building_address.method) }}</small>
    </div>
  </div>
  <div v-else-if="item.address_search_failed" class="placeholder-cell">
    <div class="placeholder-icon">❌</div>
    <div class="placeholder-text">Адрес не найден</div>
  </div>
  <div v-else class="placeholder-cell">
    <div class="placeholder-icon">🏠</div>
    <div class="placeholder-text">Поиск адреса...</div>
  </div>
</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div class="results-actions">
                      <button @click="exportToExcel" class="process-btn primary">
                        📊 Скачать Excel отчет
                      </button>
                      <button @click="currentStep = 3" class="process-btn secondary">
                        Назад к таблице
                      </button>
                      <button @click="closeModal" class="process-btn secondary">
                        Завершить
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Индикатор загрузки -->
              <div v-if="processing" class="upload-progress">
                <div class="progress-spinner"></div>
                <p>Обработка на сервере...</p>
                <p class="progress-text">{{ progressText }}</p>
              </div>
            </div>
          </div>

          <!-- Навигация -->
          <div class="step-navigation">
            <button v-if="currentStep > 1 && currentStep < 3 && selectedWorkMode !== 'coordinates_only'" 
                    @click="previousStep" 
                    class="nav-btn">
              Назад
            </button>
            
            <button v-if="currentStep === 3 && selectedWorkMode === 'zip_data'" 
                    @click="currentStep = 2" 
                    class="nav-btn">
              Назад к загрузке
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import * as XLSX from 'xlsx'

export default {
  name: 'UserUpload',
  props: {
    showUploadModal: Boolean
  },
  emits: ['close', 'upload-complete'],
  setup(props, { emit }) {
    const auth = useAuthStore()
    
    // Основные реактивные данные
    const currentStep = ref(1)
    const selectedWorkMode = ref('zip_data')
    const processing = ref(false)
    const progressText = ref('')
    const searchQuery = ref('')
    
    // Файлы для разных режимов
    const zipFile = ref(null)
    const dataFile = ref(null)
    const singleImageFile = ref(null)
    
    // Данные для предпросмотра
    const matchedData = ref([])
    const uploadResult = ref({})
    const coordinatesData = ref([])
    const coordinatesResults = ref([])
    const singleImageResult = ref({})
    
    const singleImageCoords = ref({
      lat: null,
      lon: null
    })

    // Refs для input элементов и canvas
    const zipFileInput = ref(null)
    const dataFileInput = ref(null)
    const singleImageInput = ref(null)
    const resultCanvas = ref(null)

    // Режимы работы
    const workModes = [
      {
        value: 'zip_data',
        label: 'ZIP архив + данные',
        icon: '📦',
        description: 'Загрузка ZIP архива с изображениями и файла с координатами (XLS, JSON)'
      }
    ]

    // Вычисляемые свойства
    const filteredMatchedData = computed(() => {
      if (!searchQuery.value) return matchedData.value
      return matchedData.value.filter(item => 
        item.image_name.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
    })

    // ФИЛЬТРАЦИЯ ЗДАНИЙ - убираем "Неизвестно"
    const filteredBuildings = (item) => {
      if (!item.nearby_buildings) return []
      return item.nearby_buildings.filter(building => 
        building.name && 
        !building.name.toLowerCase().includes('неизвестно') &&
        building.name.trim() !== '' &&
        building.name !== 'Unknown'
      )
    }

    // Методы выбора режима
    const selectWorkMode = (mode) => {
      selectedWorkMode.value = mode
      currentStep.value = 2
      resetFiles()
    }

    const resetFiles = () => {
      zipFile.value = null
      dataFile.value = null
      singleImageFile.value = null
      matchedData.value = []
      coordinatesData.value = []
      coordinatesResults.value = []
      singleImageResult.value = {}
      searchQuery.value = ''
    }

    // Триггеры input
    const triggerZipInput = () => zipFileInput.value?.click()
    const triggerDataFileInput = () => dataFileInput.value?.click()
    const triggerSingleImageInput = () => singleImageInput.value?.click()

    // Обработчики файлов
    const handleZipSelect = (event) => {
      const file = event.target.files[0]
      if (file && file.name.toLowerCase().endsWith('.zip')) {
        zipFile.value = file
      }
    }

    const handleDataFileSelect = async (event) => {
      const file = event.target.files[0]
      if (file) {
        dataFile.value = file
        
        // Для режима координат сразу парсим данные
        if (selectedWorkMode.value === 'coordinates_only') {
          await parseCoordinatesFile(file)
        }
        
        // Для ZIP режима загружаем и сопоставляем данные
        if (selectedWorkMode.value === 'zip_data' && zipFile.value) {
          await uploadAndMatchZipData()
        }
      }
    }

    // ИСПРАВЛЕННЫЙ МЕТОД CONFIDENCE - более строгие критерии
    const getConfidenceClass = (confidence) => {
      if (confidence >= 0.2) return 'high-confidence'    // Высокая уверенность
      if (confidence >= 0.01) return 'medium-confidence'  // Средняя уверенность  
      if (confidence >= 0.001) return 'low-confidence'     // Низкая уверенность
      return 'very-low-confidence'                       // Очень низкая уверенность
    }

    const getMethodName = (method) => {
      const methods = {
        'image_comparison': 'Сравнение изображений',
        'distance_based': 'По расстоянию',
        'yandex_search': 'Поиск Яндекс',
        'image_comparison_with_coords': 'Сравнение + координаты'
      }
      return methods[method] || method
    }

    const handleSingleImageSelect = (event) => {
      const file = event.target.files[0]
      if (file && file.type.startsWith('image/')) {
        singleImageFile.value = file
      }
    }

    // Drag & Drop handlers
    const handleZipDrop = (event) => {
      event.preventDefault()
      const files = Array.from(event.dataTransfer.files)
      const zip = files.find(f => f.name.toLowerCase().endsWith('.zip'))
      if (zip) {
        zipFile.value = zip
        if (dataFile.value) {
          uploadAndMatchZipData()
        }
      }
    }

    const handleDataFileDrop = async (event) => {
      event.preventDefault()
      const files = Array.from(event.dataTransfer.files)
      const dataFile = files.find(f => 
        f.name.toLowerCase().endsWith('.json') ||
        f.name.toLowerCase().endsWith('.geojson') ||
        f.name.toLowerCase().endsWith('.xlsx') ||
        f.name.toLowerCase().endsWith('.xls') ||
        f.name.toLowerCase().endsWith('.csv')
      )
      if (dataFile) {
        dataFile.value = dataFile
        if (selectedWorkMode.value === 'coordinates_only') {
          await parseCoordinatesFile(dataFile)
        } else if (selectedWorkMode.value === 'zip_data' && zipFile.value) {
          await uploadAndMatchZipData()
        }
      }
    }

    const formatCoordinates = (coords) => {
      if (!coords) return 'Нет данных';
      
      try {
        // Обрабатываем разные форматы координат
        if (Array.isArray(coords)) {
          if (coords.length >= 2) {
            // Формат [lon, lat] или [lat, lon]
            const lat = typeof coords[0] === 'number' ? coords[0] : coords[1];
            const lon = typeof coords[1] === 'number' ? coords[1] : coords[0];
            return `${lat?.toFixed(6)}, ${lon?.toFixed(6)}`;
          }
        } else if (coords.lat && coords.lon) {
          // Формат {lat: ..., lon: ...}
          return `${coords.lat.toFixed(6)}, ${coords.lon.toFixed(6)}`;
        } else if (coords.x && coords.y) {
          // Формат {x: ..., y: ...}
          return `${coords.y.toFixed(6)}, ${coords.x.toFixed(6)}`;
        }
        
        return 'Неверный формат координат';
      } catch (e) {
        console.error('Ошибка форматирования координат:', e, coords);
        return 'Ошибка координат';
      }
    }

    const handleSingleImageDrop = (event) => {
      event.preventDefault()
      const files = Array.from(event.dataTransfer.files)
      const image = files.find(f => f.type.startsWith('image/'))
      if (image) {
        singleImageFile.value = image
      }
    }

    // Парсинг файла с координатами
    const parseCoordinatesFile = async (file) => {
      try {
        processing.value = true
        progressText.value = 'Чтение файла с координатами...'
        
        const formData = new FormData()
        formData.append('data_file', file)
        formData.append('mode', 'parse_coordinates')
        formData.append('user_id', auth.user?.id || 'anonymous')

        const response = await fetch('https://hack2025.easy-sat.ru/api/upload', {
          method: 'POST',
          body: formData,
        })

        if (response.ok) {
          const result = await response.json()
          coordinatesData.value = result.coordinates_data || []
        } else {
          throw new Error('Ошибка чтения файла')
        }
      } catch (error) {
        console.error('Error parsing coordinates file:', error)
        alert('Ошибка при чтении файла с координатами')
      } finally {
        processing.value = false
      }
    }

    // Загрузка и сопоставление ZIP + данных
    const uploadAndMatchZipData = async () => {
      if (!zipFile.value || !dataFile.value) return
      
      processing.value = true
      progressText.value = 'Загрузка и сопоставление данных...'

      try {
        const formData = new FormData()
        formData.append('zip_file', zipFile.value)
        formData.append('data_file', dataFile.value)
        formData.append('mode', 'upload_and_match')
        formData.append('user_id', auth.user?.id || 'anonymous')

        const response = await fetch('https://hack2025.easy-sat.ru/api/upload', {
          method: 'POST',
          body: formData,
        })

        if (response.ok) {
          const result = await response.json()
          matchedData.value = result.matched_data || []
          uploadResult.value = result
          currentStep.value = 3
        } else {
          throw new Error('Ошибка сопоставления данных')
        }
      } catch (error) {
        console.error('Upload and match error:', error)
        alert('Ошибка при загрузке и сопоставлении данных')
      } finally {
        processing.value = false
      }
    }

    // МЕТОД ДЛЯ ПОИСКА БЛИЖАЙШЕГО ЗДАНИЯ (FALLBACK)
    const findNearestBuildingAddress = async (item) => {
      try {
        const validBuildings = filteredBuildings(item);
        
        if (validBuildings.length === 0) {
          console.log('Нет валидных зданий для отображения');
          return null;
        }
        
        // Берем ближайшее здание
        const closestBuilding = validBuildings[0];
        
        // Формируем топ-3 совпадения из ближайших зданий
        const topMatches = validBuildings.slice(0, 3).map((building) => ({
          name: building.name,
          // ИСПРАВЛЕННЫЙ CONFIDENCE - более консервативный расчет
          confidence: (Math.max(0.01, 1 - (building.distance / 50)) * 100).toFixed(1) + '%',
          distance: building.distance ? building.distance.toFixed(1) + 'м' : null,
          // ДОБАВЛЯЕМ КООРДИНАТЫ ДЛЯ КАЖДОГО СОВПАДЕНИЯ
          coordinates: building.tags ? { lat: building.tags.lat, lon: building.tags.lon } : null
        }));
        
        const result = {
          street: closestBuilding.name,
          // ИСПРАВЛЕННЫЙ CONFIDENCE - более консервативный
          confidence: Math.max(0.01, 1 - (closestBuilding.distance / 50)),
          method: 'distance_based',
          objects_compared: validBuildings.length,
          successful_matches: 1,
          // ДОБАВЛЯЕМ КООРДИНАТЫ ДЛЯ ОСНОВНОГО АДРЕСА
          coordinates: closestBuilding.tags ? { lat: closestBuilding.tags.lat, lon: closestBuilding.tags.lon } : null,
          top_3_matches: topMatches
        };
        
        console.log('Найден адрес по расстоянию:', result);
        return result;
        
      } catch (error) {
        console.error('Ошибка поиска по расстоянию:', error);
        return null;
      }
    };

    // ИСПРАВЛЕННЫЙ МЕТОД ДЛЯ ПОИСКА АДРЕСА
// ИСПРАВЛЕННЫЙ МЕТОД ДЛЯ ПОИСКА АДРЕСА
const searchBuildingAddress = async (item, uploadId) => {
  try {
    const catalogId = uploadId;
    
    if (!catalogId) {
      console.error('upload_id (catalog_id) не передан');
      return await findNearestBuildingAddress(item);
    }

    console.log(`Поиск адреса для image: ${item.image_name}, catalog: ${catalogId}`);

    // 1. Читаем GeoJSON файл
    const imageId = item.image_name.replace(/\.[^/.]+$/, "")
    const geojsonUrl = `https://hack2025.easy-sat.ru/storage/r/99/geojson/${imageId}.geojson`;
    const geojsonResponse = await fetch(geojsonUrl);
    
    if (!geojsonResponse.ok) {
      console.log(`GeoJSON не найден для ${imageId}, используем ближайшее здание`);
      return await findNearestBuildingAddress(item);
    }
    
    const geojsonData = await geojsonResponse.json();
    
    // 2. Ищем объекты зданий
    const buildingObjects = geojsonData.features?.filter(feature => {
      const props = feature.properties || {};
      const className = (props.class || props.class_name || props.label || '').toLowerCase();
      return className.includes('building') || className.includes('здание');
    }) || [];
    
    console.log(`Найдено ${buildingObjects.length} зданий для ${imageId}`);
    
    if (buildingObjects.length === 0) {
      console.log('Нет объектов зданий в GeoJSON, используем ближайшее здание');
      return await findNearestBuildingAddress(item);
    }
    
    // 3. Получаем изображения окружающих зданий (только валидные)
    const validBuildings = filteredBuildings(item);
    const buildingImages = validBuildings.slice(0, 10).map(building => ({
      image_url: building.images?.[0]?.thumbnail_url || building.images?.[0]?.image_url || '',
      name: building.name,
      // ИСПРАВЛЕННЫЙ ФОРМАТ КООРДИНАТ - должен быть массивом [lon, lat]
      coordinates: building.tags ? [building.tags.lon, building.tags.lat] : null,
      address: building.address || '',
      distance: building.distance || 0
    })).filter(img => img.image_url); // Фильтруем только с изображениями

    if (buildingImages.length === 0) {
      console.log('Нет изображений для сравнения, используем ближайшее здание');
      return await findNearestBuildingAddress(item);
    }

    // 4. Для первого объекта запускаем поиск
    const buildingObject = buildingObjects[0];
    const props = buildingObject.properties || {};
    const objectId = props.object_id || props.id || props.OBJECTID || 0;
    
    // ЗАПРОС С ИСПРАВЛЕННЫМ ФОРМАТОМ КООРДИНАТ
    const requestData = {
      catalog_id: catalogId,
      image_name: item.image_name,
      object_id: objectId,
      building_images: buildingImages,
      min_confidence: 0.0001,
      geojson_name: `${imageId}.geojson`
    };
    
    console.log('Отправляем запрос на поиск адреса:', requestData);
    
    const searchResponse = await fetch('https://hack2025.easy-sat.ru/api/search-by-object', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData)
    });
    
    if (searchResponse.ok) {
      const result = await searchResponse.json();
      console.log('Результат поиска адреса:', result);
      
      if (result.success && result.building_comparison) {
        const bestMatch = result.building_comparison.best_match;
        const topMatches = result.building_comparison.top_matches || [];
        
        // ИСПРАВЛЕННЫЙ CONFIDENCE - более строгий порог
        if (bestMatch && bestMatch.confidence >= 0.0001) {
          // ФИЛЬТРУЕМ СОВПАДЕНИЯ - убираем "Неизвестно"
          const filteredTopMatches = topMatches
            .filter(match => match.building_name && 
                            !match.building_name.toLowerCase().includes('неизвестно') &&
                            match.building_name !== 'Unknown')
            .slice(0, 3);

          // НАХОДИМ КООРДИНАТЫ ДЛЯ КАЖДОГО СОВПАДЕНИЯ ИЗ NEARBY_BUILDINGS
          const topMatchesWithCoords = filteredTopMatches.map(match => {
            // Ищем здание с таким же именем в nearby_buildings чтобы получить координаты
            const buildingWithCoords = validBuildings.find(b => 
              b.name === match.building_name || 
              b.name.includes(match.building_name) ||
              match.building_name.includes(b.name)
            );
            
            return {
              name: match.building_name || 'Неизвестно',
              confidence: (match.confidence * 100).toFixed(1) + '%',
              distance: match.distance ? match.distance.toFixed(1) + 'м' : null,
              // ДОБАВЛЯЕМ КООРДИНАТЫ ИЗ NEARBY_BUILDINGS
              coordinates: buildingWithCoords?.tags ? 
                { lat: buildingWithCoords.tags.lat, lon: buildingWithCoords.tags.lon } : 
                match.coordinates || match.building_coordinates
            };
          });

          // НАХОДИМ КООРДИНАТЫ ДЛЯ ОСНОВНОГО АДРЕСА
          const mainBuildingWithCoords = validBuildings.find(b => 
            b.name === bestMatch.building_name || 
            b.name.includes(bestMatch.building_name) ||
            bestMatch.building_name.includes(b.name)
          );

          const addressData = {
            street: bestMatch.building_name || 'Не определен',
            confidence: bestMatch.confidence || 0,
            method: result.building_comparison.method || 'image_comparison',
            objects_compared: result.analysis?.total_comparisons || 0,
            successful_matches: result.analysis?.successful_matches || 0,
            // ДОБАВЛЯЕМ КООРДИНАТЫ ДЛЯ ОСНОВНОГО АДРЕСА ИЗ NEARBY_BUILDINGS
            coordinates: mainBuildingWithCoords?.tags ? 
              { lat: mainBuildingWithCoords.tags.lat, lon: mainBuildingWithCoords.tags.lon } : 
              bestMatch.coordinates || bestMatch.building_coordinates,
            top_3_matches: topMatchesWithCoords
          };
          
          console.log('Успешно нашли адрес через сравнение с confidence:', bestMatch.confidence);
          console.log('Координаты основного адреса:', addressData.coordinates);
          return addressData;
        } else {
          console.log('Confidence слишком низкий:', bestMatch?.confidence, ', используем ближайшее здание');
          return await findNearestBuildingAddress(item);
        }
      } else {
        console.log('Сравнение изображений не дало результатов, используем ближайшее здание');
        return await findNearestBuildingAddress(item);
      }
    } else {
      const errorText = await searchResponse.text();
      console.log('Ошибка HTTP при поиске адреса:', searchResponse.status, errorText);
      return await findNearestBuildingAddress(item);
    }
    
  } catch (error) {
    console.error(`Ошибка поиска адреса для ${item.image_name}:`, error);
    return await findNearestBuildingAddress(item);
  }
};

    // ОБНОВЛЕННЫЙ МЕТОД ОБРАБОТКИ
    const startZipProcessing = async () => {
      if (matchedData.value.length === 0) return
      
      processing.value = true

      try {
        currentStep.value = 3
        
        // 1. Получаем nearby-objects
        for (let i = 0; i < matchedData.value.length; i++) {
          const item = matchedData.value[i]
          
          if (item.lat && item.lon) {
            progressText.value = `Поиск объектов рядом: ${i+1}/${matchedData.value.length}`
            
            const params = new URLSearchParams({
              lat: item.lat,
              lon: item.lon
            });
            
            const response = await fetch(`https://hack2025.easy-sat.ru/api/nearby-objects?${params}`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
              const nearbyData = await response.json()
              
              const nearbyAddresses = nearbyData.streets?.map(street => street.name) || []
              
              // СОХРАНЯЕМ ЗДАНИЯ С КООРДИНАТАМИ ИЗ ТЕГОВ
              const nearbyBuildings = nearbyData.buildings?.map(building => ({
                name: building.name,
                distance: building.distance,
                address: building.tags?.['full_address'] || building.tags?.['addr:street'] || '',
                // СОХРАНЯЕМ КООРДИНАТЫ ИЗ ТЕГОВ
                tags: building.tags ? {
                  lat: building.tags.lat,
                  lon: building.tags.lon,
                  'full_address': building.tags.full_address,
                  'addr:street': building.tags['addr:street'],
                  'addr:housenumber': building.tags['addr:housenumber'],
                  'addr:city': building.tags['addr:city']
                } : null,
                coordinates: building.tags ? { lat: building.tags.lat, lon: building.tags.lon } : null,
                images: building.yandex_search_results?.images?.slice(0, 3) || []
              })) || []
              
              matchedData.value[i] = {
                ...matchedData.value[i],
                nearby_addresses: nearbyAddresses,
                nearby_buildings: nearbyBuildings, // Не фильтруем здесь, фильтруем при отображении
                addresses_processed: true
              }
            }
          }
          
          await new Promise(resolve => setTimeout(resolve, 50))
        }

        // 2. Запускаем task_1 и получаем сегментацию
        progressText.value = 'Запуск анализа изображений...'
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30 * 60 * 1000);
        
        const response = await fetch('https://hack2025.easy-sat.ru/api/task_1', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...uploadResult.value,
            matched_data: matchedData.value
          }),
          signal: controller.signal,
        })

        clearTimeout(timeoutId);

        if (response.ok) {
          const result = await response.json()
          
          if (result === 'ok' || result.success) {
            // Добавляем сегментацию и аннотации
            for (let i = 0; i < matchedData.value.length; i++) {
              const item = matchedData.value[i]
              const imageId = item.image_name.replace(/\.[^/.]+$/, "")
              
              progressText.value = `Добавление результатов: ${i+1}/${matchedData.value.length}`
              
              matchedData.value[i] = {
                ...matchedData.value[i],
                segmentation_map: `https://hack2025.easy-sat.ru/storage/r/99/segm/${imageId}_seg_clr.png`,
                annotations: `https://hack2025.easy-sat.ru/storage/r/99/segm/${imageId}_t.png`,
                segmentation_processed: true
              }
              
              await new Promise(resolve => setTimeout(resolve, 20))
            }

            // 3. Поиск адресов
            progressText.value = 'Поиск адресов...'

            const uploadId = uploadResult.value.upload_id;

            for (let i = 0; i < matchedData.value.length; i++) {
              const item = matchedData.value[i]
              
              progressText.value = `Поиск адреса: ${i+1}/${matchedData.value.length}`
              
              // ВСЕГДА пытаемся найти адрес
              const addressResult = await searchBuildingAddress(item, uploadId)
              
              if (addressResult) {
                matchedData.value[i] = {
                  ...matchedData.value[i],
                  building_address: addressResult,
                  image_search_processed: true,
                  address_search_failed: false
                }
                console.log(`✅ Найден адрес для ${item.image_name}:`, addressResult.street);
              } else {
                matchedData.value[i] = {
                  ...matchedData.value[i],
                  address_search_failed: true,
                  image_search_processed: true
                }
                console.log(`❌ Адрес не найден для ${item.image_name}`);
              }
              
              await new Promise(resolve => setTimeout(resolve, 1000))
            }

            currentStep.value = 4
            progressText.value = `Все данные обработаны! Обработано ${matchedData.value.length} изображений`
          }
        }
        
      } catch (error) {
        console.error('Processing error:', error)
        alert('Ошибка при обработке изображений: ' + error.message)
      } finally {
        processing.value = false
      }
    };

    // Экспорт в Excel
    const exportToExcel = () => {
      try {
        // Подготавливаем данные для Excel
        const excelData = matchedData.value.map((item, index) => {
          // Форматируем адреса
          const addresses = item.nearby_addresses 
            ? item.nearby_addresses.slice(0, 5).join('; ')
            : 'Не найдены'
          
          // Форматируем здания
          const buildings = filteredBuildings(item)
            .slice(0, 3)
            .map(b => `${b.name} (${b.distance}м)`)
            .join('; ') || 'Не найдены'
          
          // Форматируем координаты
          const coordinates = item.lat && item.lon 
            ? `${item.lat.toFixed(6)}, ${item.lon.toFixed(6)}`
            : 'Не указаны'
                
          // Вероятный адрес
          const probableAddress = item.building_address 
            ? `${item.building_address.street} (${(item.building_address.confidence * 100).toFixed(1)}%)`
            : 'Не определен'
          
          // Координаты вероятного адреса
          const probableAddressCoords = item.building_address?.coordinates 
            ? formatCoordinates(item.building_address.coordinates)
            : 'Не указаны'

          // Ссылки на изображения
          const segmentationLink = item.segmentation_map || 'Нет данных'
          const annotationsLink = item.annotations || 'Нет данных'
          const originalImageLink = item.thumbnail_url || 'Нет данных'

          return {
            '№': index + 1,
            'Имя файла': item.image_name || '—',
            'Координаты изображения': coordinates,
            'Адреса рядом': addresses,
            'Здания рядом': buildings,
            'Вероятный адрес здания': probableAddress,
            'Координаты вероятного адреса': probableAddressCoords,
            'Метод поиска': item.building_address ? getMethodName(item.building_address.method) : '—',
            'Уверенность': item.building_address ? (item.building_address.confidence * 100).toFixed(1) + '%' : '—',
            'Ссылка на сегментацию': segmentationLink,
            'Ссылка на аннотации': annotationsLink,
            'Ссылка на оригинальное изображение': originalImageLink,
            'Размер файла': item.file_size ? formatFileSize(item.file_size) : '—'
          }
        })

        // Создаем рабочую книгу
        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(excelData)
        
        // Настраиваем ширину колонок
        const colWidths = [
          { wch: 5 },   // №
          { wch: 25 },  // Имя файла
          { wch: 25 },  // Координаты изображения
          { wch: 40 },  // Адреса рядом
          { wch: 50 },  // Здания рядом
          { wch: 40 },  // Вероятный адрес
          { wch: 30 },  // Координаты вероятного адреса
          { wch: 20 },  // Метод поиска
          { wch: 15 },  // Уверенность
          { wch: 40 },  // Ссылка на сегментацию
          { wch: 40 },  // Ссылка на аннотации
          { wch: 40 },  // Ссылка на оригинал
          { wch: 15 }   // Размер файла
        ]
        ws['!cols'] = colWidths

        // Добавляем лист в книгу
        XLSX.utils.book_append_sheet(wb, ws, 'Результаты обработки')

        // Генерируем имя файла с timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:]/g, '-')
        const fileName = `результаты_обработки_${timestamp}.xlsx`

        // Сохраняем файл
        XLSX.writeFile(wb, fileName)
        
      } catch (error) {
        console.error('Excel export error:', error)
        alert('Ошибка при экспорте в Excel: ' + error.message)
      }
    }

    // Запуск обработки для координат
    const startCoordinatesProcessing = async () => {
      if (coordinatesData.value.length === 0) return
      
      processing.value = true
      progressText.value = 'Обработка координатных данных...'

      try {
        const response = await fetch('https://hack2025.easy-sat.ru/api/process_coordinates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            coordinates: coordinatesData.value,
            user_id: auth.user?.id || 'anonymous'
          })
        })

        if (response.ok) {
          const result = await response.json()
          coordinatesResults.value = result.results || []
          currentStep.value = 3
        } else {
          throw new Error('Ошибка обработки координат')
        }
      } catch (error) {
        console.error('Coordinates processing error:', error)
        alert('Ошибка при обработке координат')
      } finally {
        processing.value = false
      }
    }

    // Запуск обработки для одиночного изображения
    const startSingleImageProcessing = async () => {
      if (!singleImageFile.value) return
      
      processing.value = true
      progressText.value = 'Загрузка и обработка изображения...'

      try {
        const formData = new FormData()
        formData.append('image_file', singleImageFile.value)
        formData.append('mode', 'process_single_image')
        formData.append('user_id', auth.user?.id || 'anonymous')
        
        if (singleImageCoords.value.lat && singleImageCoords.value.lon) {
          formData.append('lat', singleImageCoords.value.lat)
          formData.append('lon', singleImageCoords.value.lon)
        }

        const response = await fetch('https://hack2025.easy-sat.ru/api/upload', {
          method: 'POST',
          body: formData,
        })

        if (response.ok) {
          const result = await response.json()
          singleImageResult.value = result
          currentStep.value = 3
        } else {
          throw new Error('Ошибка обработки изображения')
        }
      } catch (error) {
        console.error('Single image processing error:', error)
        alert('Ошибка при обработке изображения')
      } finally {
        processing.value = false
      }
    }

    // Скачивание результатов для координат
    const downloadCoordinatesResults = () => {
      const dataStr = JSON.stringify(coordinatesResults.value, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = `coordinates_results_${new Date().getTime()}.json`
      link.click()
    }

    // Скачивание результатов для одиночного изображения
    const downloadSingleImageResults = () => {
      const dataStr = JSON.stringify(singleImageResult.value, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = `single_image_results_${new Date().getTime()}.json`
      link.click()
    }

    // Вспомогательные методы
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const getSingleImageUrl = () => {
      return singleImageFile.value ? URL.createObjectURL(singleImageFile.value) : ''
    }

    const handleImageError = (event) => {
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgdmlld0JveD0iMCAwIDI1NiAyNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyNTYiIGhlaWdodD0iMjU2IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMjggMTQ0QzEzNi44MzcgMTQ0IDE0NCAxMzYuODM3IDE0NCAxMjhDMTQ0IDExOS4xNjMgMTM2LjgzNyAxMTIgMTI4IDExMkMxMTkuMTYzIDExMiAxMTIgMTE5LjE2MyAxMTIgMTI4QzExMiAxMzYuODM3IDExOS4xNjMgMTQ0IDEyOCAxNDRaIiBmaWxsPSIjOUI5QjlCIi8+CjxwYXRoIGQ9Ik0xNjAgOTZIMTY0QzE2OC40MTggOTYgMTcyIDkyLjQxODMgMTcyIDg4VjcyQzE3MiA2Ny41ODE3IDE2OC40MTggNjQgMTY0IDY0SDE2MEMxNTUuNTgyIDY0IDE1MiA2Ny41ODE3IDE1MiA3MlY4OEMxNTIgOTIuNDE4MyAxNTUuNTgyIDk2IDE2MCA5NloiIGZpbGw9IiM5QjlCOUIiLz4KPC9zdmc+'
    }

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }

    const closeModal = () => {
      currentStep.value = 1
      selectedWorkMode.value = 'zip_data'
      resetFiles()
      processing.value = false
      emit('close')
    }

    return {
      currentStep,
      selectedWorkMode,
      processing,
      progressText,
      searchQuery,
      zipFile,
      dataFile,
      singleImageFile,
      matchedData,
      coordinatesData,
      coordinatesResults,
      singleImageResult,
      singleImageCoords,
      zipFileInput,
      dataFileInput,
      singleImageInput,
      resultCanvas,
      workModes,
      filteredMatchedData,
      filteredBuildings,
      selectWorkMode,
      triggerZipInput,
      triggerDataFileInput,
      triggerSingleImageInput,
      handleZipSelect,
      handleDataFileSelect,
      handleSingleImageSelect,
      handleZipDrop,
      handleDataFileDrop,
      handleSingleImageDrop,
      startZipProcessing,
      startCoordinatesProcessing,
      startSingleImageProcessing,
      exportToExcel,
      downloadCoordinatesResults,
      downloadSingleImageResults,
      formatFileSize,
      getSingleImageUrl,
      handleImageError,
      previousStep,
      closeModal,
      getConfidenceClass,
      getMethodName,
      formatCoordinates
    }
  }
}
</script>

<style scoped>
/* Стили для новых элементов */
.upload-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.upload-content {
  background: white;
  border-radius: 8px;
  padding: 20px;
  max-width: 1200px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
}
.no-data-message {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.no-images {
  color: #6c757d;
  font-style: italic;
}

.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.data-type-selector {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.data-type-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.data-type-card:hover {
  border-color: #007bff;
}

.data-type-card.selected {
  border-color: #007bff;
  background: #f8f9fa;
}

.type-icon {
  font-size: 32px;
  margin-bottom: 10px;
}

.type-info h5 {
  margin: 0 0 5px 0;
}

.type-info p {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
}

.file-drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  margin-bottom: 20px;
  transition: border-color 0.3s;
}

.file-drop-zone:hover {
  border-color: #007bff;
}

.drop-zone-content {
  pointer-events: none;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.select-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  pointer-events: all;
}

.file-types {
  font-size: 12px;
  color: #6c757d;
  margin-top: 10px;
}

.file-list {
  margin-top: 20px;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.remove-btn {
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.warnings-section {
  margin-top: 20px;
}

.warning-box {
  display: flex;
  align-items: flex-start;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 10px;
}

.warning-icon {
  font-size: 24px;
  margin-right: 12px;
}

.warning-text {
  color: #856404;
}

.field-mapping-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
}

.field-item {
  display: flex;
  flex-direction: column;
}

.field-item label {
  font-weight: bold;
  margin-bottom: 5px;
}

.preview-table-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
}

.preview-table th,
.preview-table td {
  padding: 8px;
  border: 1px solid #dee2e6;
  text-align: left;
}

.preview-table th {
  background: #f8f9fa;
  position: sticky;
  top: 0;
}

.selection-stats {
  margin-top: 10px;
  text-align: center;
  font-weight: bold;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 15px;
}

.image-preview {
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100px;
  object-fit: cover;
}

.image-info {
  padding: 8px;
  font-size: 12px;
}

.coords-status {
  color: #dc3545;
  font-weight: bold;
}

.summary {
  margin-bottom: 20px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.warning-text {
  color: #dc3545;
  font-weight: bold;
}

.upload-actions {
  display: flex;
  gap: 10px;
}

.upload-btn, .back-btn, .nav-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.upload-btn {
  background: #28a745;
  color: white;
}

.upload-btn:disabled {
  background: #6c757d;
}

.back-btn {
  background: #6c757d;
  color: white;
}

.nav-btn {
  background: #6c757d;
  color: white;
}

.nav-btn.primary {
  background: #007bff;
}

.step-navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
.mapping-preview {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.mapping-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 8px;
  background: white;
  border-radius: 4px;
}

.stat-item.warning {
  color: #dc3545;
  font-weight: bold;
}

.field-configuration {
  margin-bottom: 20px;
}

.image-id-field {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 2px solid #e9ecef;
}

.required-star {
  color: #dc3545;
}

.field-description {
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
}

.table-controls {
  display: flex;
  justify-content: between;
  margin-bottom: 15px;
  gap: 15px;
}

.search-box input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 300px;
}

.filter-controls label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mapping-table-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
}

.mapping-table th,
.mapping-table td {
  padding: 8px;
  border: 1px solid #dee2e6;
  text-align: left;
}

.mapping-table th {
  background: #f8f9fa;
  position: sticky;
  top: 0;
}

.mapping-table tr.matched {
  background: #d4edda;
}

.mapping-table tr.unmatched {
  background: #f8d7da;
}

.mapping-table tr.selected {
  background: #cce7ff;
}

.data-id {
  font-family: monospace;
  font-size: 12px;
}

.coordinates {
  font-family: monospace;
  font-size: 11px;
}

.no-coords {
  color: #6c757d;
  font-style: italic;
}

.timestamp {
  font-size: 12px;
}

.image-assignment select {
  width: 100%;
  padding: 4px;
}

.matched-image {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #d4edda;
  padding: 4px 8px;
  border-radius: 4px;
}

.unassign-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 12px;
}

.status-complete { color: #28a745; font-weight: bold; }
.status-partial { color: #ffc107; font-weight: bold; }
.status-incomplete { color: #dc3545; font-weight: bold; }

.selection-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.summary-stats {
  font-weight: bold;
}

.summary-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 8px 16px;
  border: 1px solid #007bff;
  background: white;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.action-btn.secondary {
  border-color: #6c757d;
  color: #6c757d;
}

.upload-summary {
  display: grid;
  gap: 20px;
}

.summary-section {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 10px;
}

.status-grid {
  display: grid;
  gap: 8px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
}

.status-item.complete { background: #d4edda; }
.status-item.partial { background: #fff3cd; }
.status-item.incomplete { background: #f8d7da; }

.status-count {
  font-weight: bold;
}

/* Остальные стили из предыдущего компонента */
.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.data-type-selector {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.data-type-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.data-type-card:hover {
  border-color: #007bff;
}

.data-type-card.selected {
  border-color: #007bff;
  background: #f8f9fa;
}

.type-icon {
  font-size: 32px;
  margin-bottom: 10px;
}

.type-info h5 {
  margin: 0 0 5px 0;
}

.type-info p {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
}

.file-upload-section {
  margin-bottom: 20px;
}

.upload-section {
  margin-bottom: 25px;
}

.file-drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  margin-bottom: 20px;
  transition: border-color 0.3s;
}

.file-drop-zone:hover {
  border-color: #007bff;
}

.drop-zone-content {
  pointer-events: none;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.select-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  pointer-events: all;
}

.file-selected {
  color: #28a745;
  font-weight: bold;
  margin-bottom: 10px;
}

.file-types {
  font-size: 12px;
  color: #6c757d;
  margin-top: 10px;
}

.file-list {
  margin-top: 15px;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  gap: 10px;
}

.file-name {
  flex: 1;
}

.file-size {
  color: #6c757d;
  font-size: 12px;
}

.file-id {
  font-family: monospace;
  font-size: 11px;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
}

.remove-btn {
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.warning-box {
  display: flex;
  align-items: flex-start;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 10px;
}

.warning-icon {
  font-size: 24px;
  margin-right: 12px;
}

.warning-text {
  color: #856404;
}

.field-mapping-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.field-item {
  display: flex;
  flex-direction: column;
}

.field-item label {
  font-weight: bold;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-item select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.upload-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.upload-btn, .back-btn, .nav-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.upload-btn {
  background: #28a745;
  color: white;
}

.upload-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.back-btn {
  background: #6c757d;
  color: white;
}

.nav-btn {
  background: #6c757d;
  color: white;
}

.nav-btn.primary {
  background: #007bff;
}

.step-navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
.upload-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px;
}

.matched-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin: 20px 0;
  max-height: 400px;
  overflow-y: auto;
}

.matched-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background: white;
  transition: transform 0.2s;
}

.matched-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.image-thumbnail {
  width: 100%;
  height: 120px;
  margin-bottom: 10px;
  border-radius: 4px;
  overflow: hidden;
}

.image-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-details {
  font-size: 12px;
}

.file-name {
  font-weight: bold;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.coordinates {
  margin-bottom: 5px;
}

.coord-label {
  color: #666;
  font-size: 11px;
}

.coord-value {
  font-family: monospace;
  font-size: 11px;
  color: #28a745;
}

.file-size {
  color: #888;
  font-size: 11px;
}

.preview-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.process-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.process-btn.primary {
  background: #007bff;
  color: white;
}

.process-btn.primary:hover:not(:disabled) {
  background: #0056b3;
}

.process-btn.secondary {
  background: #6c757d;
  color: white;
}

.process-btn.secondary:hover:not(:disabled) {
  background: #545b62;
}

.process-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.process-btn.large {
  padding: 15px 30px;
  font-size: 16px;
}

.coordinates-table-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin: 15px 0;
}

.coordinates-table {
  width: 100%;
  border-collapse: collapse;
}

.coordinates-table th,
.coordinates-table td {
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  text-align: left;
}

.coordinates-table th {
  background: #f8f9fa;
  position: sticky;
  top: 0;
  font-weight: 600;
}

.table-hint {
  text-align: center;
  padding: 8px;
  color: #6c757d;
  font-size: 12px;
  background: #f8f9fa;
}

.coordinates-actions,
.single-image-actions {
  text-align: center;
  margin-top: 20px;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.preview-controls input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 250px;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.coord-input {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
}

/* Остальные стили остаются такими же как в предыдущей версии */
.work-mode-selector {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.work-mode-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.work-mode-card:hover {
  border-color: #007bff;
}

.work-mode-card.selected {
  border-color: #007bff;
  background: #f8f9fa;
}

.mode-icon {
  font-size: 32px;
  margin-bottom: 10px;
}

.file-upload-sections {
  display: grid;
  gap: 20px;
}

.upload-section {
  margin-bottom: 25px;
}

.file-drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  transition: border-color 0.3s;
}

.file-drop-zone:hover {
  border-color: #007bff;
}

.file-drop-zone.single-image {
  padding: 20px;
}

.image-preview-large {
  margin-bottom: 15px;
}

.image-preview-large img {
  max-width: 300px;
  max-height: 200px;
  border-radius: 4px;
}

.upload-progress {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 20px 0;
}

.progress-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.coordinates-input {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.coords-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 10px;
}

.coord-field {
  display: flex;
  flex-direction: column;
}

.coord-field label {
  font-weight: bold;
  margin-bottom: 5px;
}

.coords-hint {
  font-size: 12px;
  color: #6c757d;
  margin-top: 10px;
}

.step-navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding: 20px;
  border-top: 1px solid #eee;
}

.nav-btn {
  padding: 10px 20px;
  border: 1px solid #007bff;
  background: white;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
}

.nav-btn.primary {
  background: #007bff;
  color: white;
}

.nav-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.upload-info {
  background: #e7f3ff;
  padding: 12px;
  border-radius: 4px;
  margin-top: 15px;
  font-size: 14px;
}

.results-table-container {
  width: 100%;
  overflow-x: auto;
  margin: 20px 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1200px;
}

.results-table th {
  background: #f8f9fa;
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  font-size: 12px;
  line-height: 1.2;
}

.results-table td {
  padding: 8px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: top;
  height: 120px;
}

/* Столбцы с фиксированной шириной */
.col-image { width: 200px; }
.col-addresses { width: 180px; }
.col-buildings { width: 160px; }
.col-segmentation { width: 150px; }
.col-annotations { width: 150px; }
.col-building-address { width: 160px; }

/* Стили для ячеек */
.user-data-cell {
  background: #f8f9fa;
}

.user-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.image-thumbnail-table img {
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.file-info-table {
  text-align: center;
}

.file-name-table {
  font-size: 11px;
  font-weight: 500;
  margin-bottom: 4px;
  word-break: break-all;
}

.coordinates-table {
  font-size: 10px;
  color: #666;
}

.file-size-table {
  font-size: 10px;
  color: #888;
}

/* Заглушки */
.placeholder-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6c757d;
  text-align: center;
  padding: 10px;
}

.placeholder-icon {
  font-size: 20px;
  margin-bottom: 5px;
}

.placeholder-text {
  font-size: 11px;
  line-height: 1.2;
}

.process-single-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
}

.process-single-btn:hover {
  background: #0056b3;
}

/* Стили для данных */
.addresses-list,
.buildings-list,
.annotations-list {
  font-size: 11px;
}

.address-item,
.building-item,
.annotation-item {
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.building-name {
  font-weight: 500;
}

.building-distance {
  font-size: 10px;
  color: #666;
}

.annotation-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.annotation-confidence {
  font-size: 10px;
  color: #28a745;
  font-weight: 500;
}

.more-addresses,
.more-buildings,
.more-annotations {
  font-size: 10px;
  color: #007bff;
  margin-top: 4px;
}

.segmentation-preview img {
  width: 100%;
  max-width: 120px;
  height: auto;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.building-address {
  font-size: 11px;
  text-align: center;
}

.address-main {
  font-weight: 500;
  margin-bottom: 4px;
}

.address-details {
  font-size: 10px;
  color: #666;
  margin-bottom: 4px;
}

.address-confidence {
  font-size: 10px;
  color: #28a745;
}
.building-thumbnail {
  width: 40px;
  height: 30px;
  object-fit: cover;
  border-radius: 2px;
  margin-top: 4px;
  border: 1px solid #ddd;
}

.building-images {
  margin-top: 4px;
}
.results-container {
  margin-top: 20px;
}

.results-summary {
  background: #e8f5e8;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.results-summary h5 {
  color: #2e7d32;
  margin: 0 0 10px 0;
}

.results-summary p {
  color: #1b5e20;
  margin: 0;
}

.results-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.address-method,
.objects-compared {
  font-size: 9px;
  color: #666;
  margin-top: 2px;
}

.objects-compared {
  color: #007bff;
}

.confidence-badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.8em;
  margin-left: 8px;
}

.high-confidence { background: #d4edda; color: #155724; }
.medium-confidence { background: #fff3cd; color: #856404; }
.low-confidence { background: #f8d7da; color: #721c24; }
.very-low-confidence { background: #e2e3e5; color: #383d41; }

.top-matches {
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.matches-title {
  font-size: 0.9em;
  font-weight: bold;
  margin-bottom: 4px;
}

.match-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.8em;
  margin-bottom: 2px;
}

.match-name {
  flex: 1;
}

.match-confidence {
  color: #666;
}

.search-stats, .search-method {
  margin-top: 4px;
  color: #666;
}

/* Существующие стили + новые */
.coordinates-info {
  margin-top: 4px;
  color: #666;
  font-size: 0.8em;
}

.match-main {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.match-distance {
  color: #007bff;
  font-size: 0.8em;
  margin-top: 2px;
}

.match-coords {
  color: #666;
  font-size: 0.7em;
  margin-top: 1px;
}

.top-matches {
  margin-top: 8px;
  border-top: 1px solid #eee;
  padding-top: 8px;
}

.matches-title {
  font-size: 0.8em;
  font-weight: bold;
  margin-bottom: 4px;
  color: #666;
}

.match-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 6px;
  padding: 4px;
  background: #f8f9fa;
  border-radius: 4px;
}

.match-name {
  font-weight: 500;
  font-size: 0.9em;
}

.match-confidence {
  color: #007bff;
  font-size: 0.8em;
}

.match-coords {
  color: #666;
  font-size: 0.7em;
  margin-top: 2px;
}
</style>